import React, { useCallback, useEffect, useRef, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { kioskService } from "../services/dataService";
import { useQrScanner } from "../components/QrScanner";
import { PunchKind } from "../types";
import "./Kiosk.css";

/**
 * Kapı tableti — oturumsuz. Adres: /kiosk?key=<cihaz anahtarı>.
 * Kamera açıkken QR aranır (jsQR); okununca vuruş gönderilir ve büyük bir onay kartı
 * gösterilir. Kamera yoksa ya da izin verilmezse sicil no + PIN ile giriş.
 * Anahtar yalnızca bellekte tutulur; sayfa yenilenince adresten tekrar alınır.
 */

type Result = { ok: boolean; name?: string; kind?: PunchKind; at?: string; message: string };

const Kiosk: React.FC = () => {
  const [params] = useSearchParams();
  const key = params.get("key") || "";
  const [info, setInfo] = useState<{ device: string; location?: string | null; pinAllowed: boolean } | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [now, setNow] = useState(new Date());
  const [result, setResult] = useState<Result | null>(null);
  const [manual, setManual] = useState(false);
  const [f, setF] = useState({ number: "", pin: "" });
  const [busy, setBusy] = useState(false);
  const busyRef = useRef(false);

  useEffect(() => { const t = setInterval(() => setNow(new Date()), 1000); return () => clearInterval(t); }, []);
  useEffect(() => {
    if (!key) { setError("Cihaz anahtarı eksik. Adres şu biçimde olmalı: /kiosk?key=…"); return; }
    kioskService.info(key).then((i) => { setInfo(i); setError(null); }).catch((e) => setError(e?.response?.data?.message || "Cihaz doğrulanamadı. Anahtarı ve ayarları kontrol edin."));
    const t = setInterval(() => kioskService.info(key).then((i) => { setInfo(i); setError(null); }).catch((e) => setError(e?.response?.data?.message || "Sunucuya ulaşılamıyor.")), 60000);
    return () => clearInterval(t);
  }, [key]);

  const send = useCallback(async (body: { qrToken?: string; employeeNumber?: string; pin?: string }) => {
    if (busyRef.current) return;
    busyRef.current = true; setBusy(true);
    try {
      const r = await kioskService.punch(key, body);
      if (r.duplicate) setResult({ ok: true, name: r.name, message: "Az önce vuruldu; tekrar gerekmez." });
      else setResult({ ok: true, name: r.name, kind: r.kind, at: r.at, message: r.kind === PunchKind.In ? "Hoş geldiniz, giriş kaydedildi." : "İyi günler, çıkış kaydedildi." });
      setF({ number: "", pin: "" }); setManual(false);
    } catch (e: any) { setResult({ ok: false, message: e?.response?.data?.message || "Kayıt yapılamadı." }); }
    finally { busyRef.current = false; setBusy(false); setTimeout(() => setResult(null), 4000); }
  }, [key]);

  // Kamera + QR tarama (ortak okuyucu: components/QrScanner) — ön kamera, yalnız "nq_" kartları, aynı kart 5 sn'de bir
  const { videoRef, canvasRef, camera } = useQrScanner({
    enabled: !!info && !manual, facingMode: "user", dedupeMs: 5000,
    accept: (d) => d.startsWith("nq_"),
    onCode: (d) => { send({ qrToken: d }); },
  });

  const submitManual = (e: React.FormEvent) => { e.preventDefault(); if (!f.number.trim() || f.pin.length < 4) return; send({ employeeNumber: f.number.trim(), pin: f.pin }); };
  const pad = (d: string) => { if (d === "⌫") setF((x) => ({ ...x, pin: x.pin.slice(0, -1) })); else if (f.pin.length < 6) setF((x) => ({ ...x, pin: x.pin + d })); };

  return (
    <div className="kiosk">
      <header className="kiosk-head">
        <div className="kiosk-brand"><span className="kiosk-mark">N</span><span>NEDARIS</span></div>
        <div className="kiosk-device">{info ? <><b>{info.device}</b>{info.location && <span>{info.location}</span>}</> : <span>Cihaz doğrulanıyor</span>}</div>
        <div className="kiosk-clock"><b>{now.toLocaleTimeString("tr-TR", { hour: "2-digit", minute: "2-digit" })}</b><span>{now.toLocaleDateString("tr-TR", { weekday: "long", day: "2-digit", month: "long" })}</span></div>
      </header>

      {error ? (
        <div className="kiosk-error"><b>Kiosk kullanılamıyor</b><span>{error}</span></div>
      ) : !info ? (
        <div className="kiosk-wait">Bağlanıyor…</div>
      ) : (
        <main className="kiosk-main">
          {!manual ? (
            <section className="kiosk-scan">
              <div className={`kiosk-video ${camera}`}>
                <video ref={videoRef} playsInline muted />
                <canvas ref={canvasRef} hidden />
                {camera === "on" && <div className="kiosk-frame" />}
                {camera === "denied" && <div className="kiosk-video-msg"><b>Kamera izni verilmedi</b><span>Tarayıcı ayarlarından kameraya izin verin ya da sicil no ile giriş yapın.</span></div>}
                {camera === "none" && <div className="kiosk-video-msg"><b>Kamera bulunamadı</b><span>Bu cihazda kamera yok; sicil no ile giriş yapın.</span></div>}
                {camera === "idle" && <div className="kiosk-video-msg"><span>Kamera açılıyor…</span></div>}
              </div>
              <h1>QR kartınızı kameraya gösterin</h1>
              <p>İlk okutma giriş, günün sonraki okutması çıkış olarak kaydedilir.</p>
              {info.pinAllowed && <button className="kiosk-alt" onClick={() => setManual(true)}>QR kartım yanımda değil, sicil no ile gireceğim</button>}
            </section>
          ) : (
            <section className="kiosk-manual">
              <form onSubmit={submitManual}>
                <h1>Sicil no ve PIN</h1>
                <label><span>Sicil no</span><input value={f.number} autoFocus autoComplete="off" onChange={(e) => setF({ ...f, number: e.target.value })} placeholder="P-0001" /></label>
                <label><span>PIN</span><input type="password" inputMode="numeric" value={f.pin} readOnly placeholder="••••" /></label>
                <div className="kiosk-pad">{["1", "2", "3", "4", "5", "6", "7", "8", "9", "⌫", "0", "OK"].map((d) => <button type={d === "OK" ? "submit" : "button"} key={d} className={d === "OK" ? "ok" : d === "⌫" ? "del" : ""} disabled={busy || (d === "OK" && (f.pin.length < 4 || !f.number.trim()))} onClick={d === "OK" ? undefined : () => pad(d)}>{d}</button>)}</div>
              </form>
              <button className="kiosk-alt" onClick={() => setManual(false)}>← QR ile okut</button>
            </section>
          )}
        </main>
      )}

      {result && (
        <div className={`kiosk-result ${result.ok ? (result.kind === PunchKind.Out ? "out" : "in") : "bad"}`}>
          <div className="kiosk-result-icon">{result.ok ? "✓" : "!"}</div>
          {result.name && <b>{result.name}</b>}
          <span>{result.message}</span>
          {result.at && <small>{new Date(result.at).toLocaleTimeString("tr-TR", { hour: "2-digit", minute: "2-digit" })}</small>}
        </div>
      )}
      <footer className="kiosk-foot">Nedaris Üretim Yönetim Sistemi · giriş-çıkış kioski</footer>
    </div>
  );
};

export default Kiosk;
